import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  styled,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { digitalApiUrl } from "../../../Services/Api";
import {
  IRestStoryInstitutionalList,
  IStoryInstitutional,
  deleteInstitutionalStory,
  getInstitutionalStories,
  postInstitutionalStory,
} from "../../../Services/Story";
import { TextH2 } from "../../../styles/global.styles";

// Mesmo teto do servidor (StoryService::LIMITE_CAPTION). Aqui serve só para
// avisar antes de enviar — quem corta de verdade é a API, porque esconder o
// limite no front não impede um POST manual.
const LIMITE_CAPTION = 100;

export const InstitutionalStories = () => {
  const [stories, setStories] = useState<IStoryInstitutional[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [enviando, setEnviando] = useState<boolean>(false);

  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [caption, setCaption] = useState<string>("");

  const carregar = useCallback(() => {
    setCarregando(true);

    getInstitutionalStories()
      .then((response) => {
        const { data } = response as IRestStoryInstitutionalList;
        setStories(data.data ?? []);
      })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
    }
  };

  const enviar = () => {
    if (!imageFile) {
      return;
    }

    const formData = new FormData();
    formData.append("photo", imageFile);
    formData.append("caption", caption);

    setEnviando(true);

    postInstitutionalStory(formData)
      .then(() => {
        setImageFile(undefined);
        setCaption("");
        carregar();
      })
      .finally(() => setEnviando(false));
  };

  const remover = (story: IStoryInstitutional) => {
    const confirmado = window.confirm(
      "Remover esta foto institucional? A imagem sai do storage e do carrossel."
    );

    if (!confirmado) {
      return;
    }

    deleteInstitutionalStory(story.id).then(() => carregar());
  };

  // A API devolve caminho relativo enquanto STORAGE_URL não estiver definida
  // no servidor, e URL completa depois que estiver. Os dois casos passam por
  // aqui sem a tela precisar saber qual está valendo.
  const urlDaImagem = (imageUrl: string | null): string => {
    if (!imageUrl) {
      return "";
    }

    return imageUrl.startsWith("http") ? imageUrl : digitalApiUrl + imageUrl;
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <Stack spacing={2}>
      <TextH2>Momentos Compartilhados — Fotos Institucionais</TextH2>

      <div
        style={{
          backgroundColor: "#e6f7f8",
          borderLeft: "4px solid #00b6bc",
          borderRadius: "4px",
          padding: "12px 16px",
          fontSize: "14px",
          color: "#055b5e",
          maxWidth: "1000px",
        }}
      >
        Estas fotos entram no carrossel da home e na primeira página do feed
        quando os usuários não publicam o suficiente nas últimas 24 horas. Não
        expiram, não têm autor nem estabelecimento, e o app não deixa denunciar
        nem apagar nenhuma delas.
      </div>

      {!carregando && stories.length === 0 && (
        <div
          style={{
            backgroundColor: "#fff4e5",
            borderLeft: "4px solid #ed6c02",
            borderRadius: "4px",
            padding: "12px 16px",
            fontSize: "14px",
            color: "#663c00",
            maxWidth: "1000px",
          }}
        >
          Nenhuma foto institucional cadastrada. Sem pelo menos uma, o carrossel
          da home fica vazio nos períodos sem publicação.
        </div>
      )}

      {/* ─── Cadastro ─────────────────────────────────────────────────────── */}

      <Grid container spacing={2} width={"1000px"} alignItems={"center"}>
        <Grid xs={3} item paddingLeft={"0px !important"}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            style={{ width: "100%", height: "56px" }}
          >
            {imageFile ? "Trocar foto" : "Escolher foto"}
            <VisuallyHiddenInput
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
              onChange={handleFileChange}
            />
          </Button>
        </Grid>

        <Grid xs={6} item>
          <TextField
            style={{ width: "100%" }}
            autoComplete="off"
            id="caption"
            label="Frase da foto (opcional)"
            variant="outlined"
            type="text"
            value={caption}
            inputProps={{ maxLength: LIMITE_CAPTION }}
            helperText={`${caption.length}/${LIMITE_CAPTION}`}
            onChange={(e) => setCaption(e.target.value)}
          />
        </Grid>

        <Grid xs={3} item>
          <Button
            variant="contained"
            disabled={!imageFile || enviando}
            onClick={enviar}
            style={{
              width: "100%",
              height: "56px",
              backgroundColor: !imageFile || enviando ? undefined : "#00b6bc",
            }}
          >
            {enviando ? "Enviando..." : "Cadastrar"}
          </Button>
        </Grid>

        {imageFile && (
          <Grid xs={12} item paddingLeft={"0px !important"}>
            <img
              src={URL.createObjectURL(imageFile)}
              alt="Pré-visualização"
              style={{
                width: "160px",
                height: "200px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #ddd",
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* ─── Lista ────────────────────────────────────────────────────────── */}

      {carregando ? (
        <CircularProgress style={{ color: "#00b6bc" }} />
      ) : (
        <Grid container spacing={2} width={"1000px"}>
          {stories.map((story) => (
            <Grid xs={3} item key={story.id}>
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                <img
                  src={urlDaImagem(story.imageUrl)}
                  alt={story.caption ?? "Foto institucional"}
                  style={{
                    width: "100%",
                    height: "260px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                <div style={{ padding: "10px 12px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#333",
                      minHeight: "36px",
                      lineHeight: "18px",
                    }}
                  >
                    {story.caption ?? (
                      <span style={{ color: "#999" }}>sem frase</span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    <span>
                      {story.likeCount} curtidas · {story.viewCount}{" "}
                      visualizações
                    </span>

                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => remover(story)}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
};